import * as React from 'react';
import {
  cn,
  formatDate,
  isRawDate,
  isRawDateTime,
  parseDate
} from '../../../utils';
import { Metadata, SchemaField } from '../../../types/Metadata';
import InfiniteLoader from '../../InfiniteLoader/infiniteLoader';
import { TrueCheck, FalseCheck } from '../../Icon/icon';
import { useSelector } from '../../../utils/useSelector';
import { Export } from '../Export/export';
import URLManager from '../../../utils/urlManager';

type Data = Metadata<{}, any, { refId?: string }>;

export const ReportTools = () => {
  return (
    <div className="view-tools">
      <div className="filters"></div>
      <div className="filter-links">
        <Export />
      </div>
    </div>
  );
};

export const Report = () => {
  const minCellWidth = 120;
  const getMountPoint = useSelector(s => s.app?.elFn?.getMountPoint);
  const data: Data[] = useSelector(s => s.view?.data);
  const selectedItem = useSelector(s => s.form?.data?.id);
  const reportFields = useSelector(s => s.view?.selected?.props?.reportFields);
  if (!reportFields) return null;
  const { fields, columns, gridTemplateColumns } = getTableInfo(reportFields);
  const wrapText = useSelector(s => s.view?.selected?.props?.wrapText);
  const { updateUrl } = URLManager.useRouter();
  const [tableHeight, setTableHeight] = React.useState<string>('auto');
  const [activeIndex, setActiveIndex] = React.useState<number>(null);
  const [page, setPage] = React.useState(1);
  const tableElement = React.useRef(null);

  React.useEffect(() => {
    setTableHeight(tableElement?.current?.offsetHeight);
  }, []);
  const _window = getMountPoint ? getMountPoint() : window;
  const mouseMove = React.useCallback(
    e => {
      const navFlyout = document.getElementById('navFlyout')?.offsetWidth;
      const gridColumns = columns.map((col, i) => {
        if (i === activeIndex) {
          const width = e.clientX - navFlyout - col.ref.current.offsetLeft;
          if (width >= minCellWidth) {
            return `${width}px`;
          }
        }
        return `${col.ref.current.offsetWidth}px`;
      });

      tableElement.current.style.gridTemplateColumns = `${gridColumns.join(
        ' '
      )}`;
    },
    [activeIndex, columns, minCellWidth]
  );

  const removeListeners = React.useCallback(() => {
    _window.removeEventListener('mousemove', mouseMove);
    _window.removeEventListener('mouseup', removeListeners);
  }, [mouseMove, _window]);

  const mouseUp = React.useCallback(() => {
    setActiveIndex(null);
    removeListeners();
  }, [setActiveIndex, removeListeners, _window]);

  React.useEffect(() => {
    if (activeIndex !== null) {
      _window.addEventListener('mousemove', mouseMove);
      _window.addEventListener('mouseup', mouseUp);
    }

    return () => {
      removeListeners();
    };
  }, [activeIndex, mouseMove, mouseUp, removeListeners, _window]);

  const ref = React.useRef(null);

  return (
    <div className="tabular" id="tabular" ref={ref}>
      <div className="table-wrapper">
        <table
          className={cn('resizeable-table readonly-table', {
            wrap: wrapText,
            'no-wrap': !wrapText
          })}
          style={{ gridTemplateColumns: gridTemplateColumns.join(' ') }}
          ref={tableElement}
        >
          <thead>
            <tr>
              {columns.map(({ ref, text }, i) => (
                <th ref={ref} key={text}>
                  <span title={wrapText ? null : text}>{text}</span>
                  <div
                    style={{ height: tableHeight }}
                    onMouseDown={() => setActiveIndex(i)}
                    className={`resize-handle ${
                      activeIndex === i ? 'active' : 'idle'
                    }`}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.slice(0, page * 25).map((item, i) => {
              return (
                <tr
                  key={i}
                  className={cn('slider-activator', {
                    'select-item': selectedItem && selectedItem == item.refId,
                    'selectable-item': !!item.refId
                  })}
                  onClick={
                    !item.refId ? null : () => updateUrl({ id: item.refId })
                  }
                >
                  {fields.map(f => (
                    <TableCell
                      key={f.field}
                      field={f.field}
                      item={item}
                      wrapText={wrapText}
                    />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
        {!!data?.length && data.length > page * 25 && (
          <InfiniteLoader
            container={ref}
            loadData={cb => {
              setPage(page + 1);
              cb();
            }}
            forceResize={wrapText}
          />
        )}
      </div>
    </div>
  );
};

type TableCellProps = {
  field: string;
  item: Metadata;
  wrapText: boolean;
};

const TableCell = ({ field, item, wrapText }: TableCellProps) => {
  const val = item[field];
  if (typeof val == 'boolean') {
    return <TD>{val ? <TrueCheck /> : <FalseCheck />}</TD>;
  } else if (isRawDate(val)) {
    const date = parseDate(val as any);
    let value = formatDate(date);
    if (date && isRawDateTime(val)) {
      value += ' ' + date.toLocaleTimeString();
    }
    return <TD title={wrapText ? null : value}>{value}</TD>;
  } else if (
    typeof val == 'string' &&
    (val.startsWith('https://') || val.startsWith('http://'))
  ) {
    return (
      <TD title={wrapText ? null : val}>
        <a target="_blank" href={val} onClick={handleStopPropagation}>
          {val}
        </a>
      </TD>
    );
  } else {
    let vals = val;
    if (Array.isArray(vals)) vals = vals.join(', ');
    return <TD title={wrapText ? null : vals}>{vals}</TD>;
  }
};

const TD: React.FC<React.HTMLProps<HTMLTableCellElement>> = ({
  children,
  title
}) => (
  <td>
    <span title={title}>{children}</span>
  </td>
);

const handleStopPropagation = (e: any) => e.stopPropagation();

const getTableInfo = (fields: SchemaField[]) => {
  const columns = [];
  const gridTemplateColumns = [];
  if (fields?.length) {
    for (const field of fields) {
      columns.push({
        text: field.name,
        ref: React.useRef() as any
      });
      gridTemplateColumns.push('minmax(120px, 2fr)');
    }
  }
  return { fields, columns, gridTemplateColumns };
};
